import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Paper,
  Chip,
  CardMedia
} from '@mui/material';
import {
  DirectionsCar,
  AccountBalance,
  LocalHospital,
  Store,
  Factory,
  Flight,
  Public,
  Business,
  Security,
  TrendingUp
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

const Industries = () => {
  const industries = [
    {
      id: 'automotive',
      title: 'Automotive',
      description: 'Helping automakers embrace cloud technologies and better connect data across mission-critical operations.',
      icon: <DirectionsCar sx={{ fontSize: 40, color: '#1976d2' }} />,
      features: ['Cloud Integration', 'Data Analytics', 'IoT Solutions', 'Digital Transformation'],
      image: '/api/placeholder/400/250',
      link: '/industries/automotive'
    },
    {
      id: 'banking-financial',
      title: 'Banking and Financial',
      description: 'Helping financial institutions modernize IT to exceed customer expectations and regulatory requirements.',
      icon: <AccountBalance sx={{ fontSize: 40, color: '#1976d2' }} />,
      features: ['Digital Banking', 'Risk Management', 'Compliance', 'Payment Solutions'],
      image: '/api/placeholder/400/250',
      link: '/industries/banking-and-financial-markets'
    },
    {
      id: 'healthcare',
      title: 'Healthcare',
      description: 'Helping providers deliver improved patient care, experiences and outcomes through technology.',
      icon: <LocalHospital sx={{ fontSize: 40, color: '#1976d2' }} />,
      features: ['Electronic Health Records', 'Telemedicine', 'Data Security', 'Patient Analytics'],
      image: '/api/placeholder/400/250',
      link: '/industries/healthcare'
    },
    {
      id: 'retail',
      title: 'Retail',
      description: 'Helping retailers become more agile and responsive to the ever-evolving needs of today\'s customers.',
      icon: <Store sx={{ fontSize: 40, color: '#1976d2' }} />,
      features: ['E-commerce Solutions', 'Inventory Management', 'Customer Analytics', 'Omnichannel'],
      image: '/api/placeholder/400/250',
      link: '/industries/retail'
    },
    {
      id: 'manufacturing',
      title: 'Manufacturing',
      description: 'Helping manufacturers digitize and modernize operations to exceed customer requirements.',
      icon: <Factory sx={{ fontSize: 40, color: '#1976d2' }} />,
      features: ['Smart Manufacturing', 'Supply Chain', 'Quality Control', 'Predictive Maintenance'],
      image: '/api/placeholder/400/250',
      link: '/industries/manufacturing'
    },
    {
      id: 'travel-transportation',
      title: 'Travel and Transportation',
      description: 'Helping travel and transportation companies build trust and loyalty through agility and reliability.',
      icon: <Flight sx={{ fontSize: 40, color: '#1976d2' }} />,
      features: ['Route Optimization', 'Customer Experience', 'Fleet Management', 'Booking Systems'],
      image: '/api/placeholder/400/250',
      link: '/industries/travel-transportation'
    },
    {
      id: 'utilities',
      title: 'Utilities',
      description: 'Helping utilities companies modernize infrastructure and reimagine grid through soaring power demand.',
      icon: <Public sx={{ fontSize: 40, color: '#1976d2' }} />,
      features: ['Smart Grid', 'Energy Management', 'Customer Service', 'Sustainability'],
      image: '/api/placeholder/400/250',
      link: '/industries/utilities'
    },
    {
      id: 'insurance',
      title: 'Insurance',
      description: 'Helping insurers modernize and transform for the future with digital solutions.',
      icon: <Security sx={{ fontSize: 40, color: '#1976d2' }} />,
      features: ['Claims Processing', 'Risk Assessment', 'Customer Portal', 'Fraud Detection'],
      image: '/api/placeholder/400/250',
      link: '/industries/insurance'
    },
    {
      id: 'communication-media',
      title: 'Technology, Media & Telecommunications',
      description: 'Powering the world\'s most vital systems and partners to transform enterprises, networks, and customer experiences.',
      icon: <Business sx={{ fontSize: 40, color: '#1976d2' }} />,
      features: ['Network Infrastructure', 'Content Delivery', '5G Solutions', 'Media Analytics'],
      image: '/api/placeholder/400/250',
      link: '/industries/communication-and-media'
    },
    {
      id: 'chemical-oil-gas',
      title: 'Chemical, Oil and Gas',
      description: 'Helping chemical, oil and gas companies modernize operations for sustainable, resilient growth.',
      icon: <TrendingUp sx={{ fontSize: 40, color: '#1976d2' }} />,
      features: ['Process Optimization', 'Safety Systems', 'Environmental Monitoring', 'Supply Chain'],
      image: '/api/placeholder/400/250',
      link: '/industries/chemical-oil-gas'
    }
  ];

  const spotlight = {
    title: 'Customer Success Story',
    company: 'Global Manufacturing Leader',
    description: 'We helped a leading manufacturer migrate from their legacy systems to cloud, improving operational efficiency by 40% and reducing costs by 25%.',
    metrics: [
      { value: '40%', label: 'Efficiency Improvement' },
      { value: '25%', label: 'Cost Reduction' },
      { value: '99.9%', label: 'Uptime Achievement' }
    ]
  };

  const stats = [
    { number: '500+', label: 'Industry Projects' },
    { number: '50+', label: 'Industry Experts' },
    { number: '15+', label: 'Years Experience' },
    { number: '95%', label: 'Client Retention' }
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          py: 12,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 'bold',
                color: '#1a1a1a',
                mb: 3,
                lineHeight: 1.2
              }}
            >
              Industries
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: '#666',
                mb: 4,
                lineHeight: 1.6,
                maxWidth: '800px',
                mx: 'auto'
              }}
            >
              Scherstellen Technologie helps modernize, digitize and secure your IT to provide outstanding customer experiences across industries
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: '#1976d2',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: '#1565c0' }
                }}
              >
                Read the Story
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    borderColor: '#1565c0',
                    color: '#1565c0',
                    bgcolor: 'rgba(25, 118, 210, 0.04)'
                  }
                }}
              >
                Read the 2024 Report
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Industries Grid */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {industries.map((industry, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardMedia
                    component="div"
                    sx={{
                      height: 200,
                      bgcolor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        borderRadius: '50%',
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {industry.icon}
                    </Box>
                    <Typography variant="h6" color="text.secondary">
                      {industry.title}
                    </Typography>
                  </CardMedia>
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        color: '#1a1a1a',
                        mb: 2
                      }}
                    >
                      {industry.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        mb: 3,
                        lineHeight: 1.6
                      }}
                    >
                      {industry.description}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 3 }}>
                      {industry.features.slice(0, 2).map((feature, idx) => (
                        <Chip
                          key={idx}
                          label={feature}
                          size="small"
                          sx={{
                            bgcolor: '#e3f2fd',
                            color: '#1976d2',
                            fontWeight: 'bold',
                            fontSize: '0.75rem'
                          }}
                        />
                      ))}
                    </Stack>
                    <Button
                      component={Link}
                      to={industry.link}
                      variant="outlined"
                      fullWidth
                      sx={{
                        borderColor: '#1976d2',
                        color: '#1976d2',
                        '&:hover': {
                          borderColor: '#1565c0',
                          color: '#1565c0',
                          bgcolor: 'rgba(25, 118, 210, 0.04)'
                        }
                      }}
                    >
                      Learn More
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Spotlight Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: 6,
              bgcolor: '#1976d2',
              color: 'white',
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 2 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 'bold',
                  mb: 2
                }}
              >
                {spotlight.title}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 1,
                  opacity: 0.9
                }}
              >
                {spotlight.company}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mb: 4,
                  opacity: 0.8,
                  lineHeight: 1.6
                }}
              >
                {spotlight.description}
              </Typography>
              <Grid container spacing={4}>
                {spotlight.metrics.map((metric, index) => (
                  <Grid item xs={12} sm={4} key={index}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 'bold',
                          mb: 1
                        }}
                      >
                        {metric.value}
                      </Typography>
                      <Typography variant="body1" sx={{ opacity: 0.9 }}>
                        {metric.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box sx={{ py: 8, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} sx={{ textAlign: 'center' }}>
            {stats.map((stat, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 'bold',
                    color: '#1976d2',
                    mb: 1
                  }}
                >
                  {stat.number}
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  {stat.label}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: '#1976d2',
              color: 'white',
              borderRadius: 2
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 'bold',
                mb: 3
              }}
            >
              Connect with Scherstellen Technologie
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.9
              }}
            >
              Together we can make sure your industry thrives.
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#1976d2',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              Contact Us
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default Industries;
